/**
 * @fileoverview The colour theme: dark (the Studio Quiet default) or light.
 *
 * Every surface in the three portals is painted from the token layer in
 * `index.css` (`--studio-*`, `--text-*`, `--accent-*`, `--state-*`), and the
 * theme is nothing more than which set of values those tokens resolve to.
 * Switching is one attribute on `<html>`: `data-theme="light"` selects the
 * light block, anything else is dark.
 *
 * Three decisions worth knowing.
 *
 * **It is a preference of the BROWSER, not of the account.** The value lives
 * in localStorage, so it applies the moment it is set, needs no request, and
 * works identically for an instructor, a student, an org admin and an ops
 * operator — four session types that share one machine's screen and would
 * otherwise need four endpoints to say the same thing. The cost is that it
 * does not follow a person to another device, which is how most apps treat a
 * display preference.
 *
 * **Light is the default, and "no preference" means light.** The OS setting
 * is deliberately NOT consulted, in either direction: the default is a
 * product decision, not a reading of the machine, so every account, portal
 * and device that has never touched the switch meets the same app. Dark is
 * still one click away and, once chosen, is remembered for that browser.
 *
 * **The attribute is set BEFORE React mounts.** `index.html` carries a tiny
 * inline script that reads the same key and stamps `<html>` before the first
 * paint, so a light-theme user never sees a dark flash. This module is the
 * runtime half: the same read, plus the write, the change notification and
 * the cross-tab sync.
 *
 * **The theme applies ONLY behind a sign-in.** The landing page, the pricing
 * and feature pages, the sign-in shell, the password flows, and every
 * tokenized public page (consent, booking, program directory, portfolio,
 * invitations, clips) are brand surfaces with no account behind them, and
 * they are Studio Quiet dark whatever this browser has stored — and whatever
 * the signed-in default happens to be, which is why `PUBLIC_THEME` is its
 * own constant rather than a second use of `DEFAULT_THEME`. That is a rule
 * about the PATH, applied in one place (`resolveTheme`), so a public page
 * cannot forget to opt out — the boot script applies the same rule with the
 * same expression, which `theme.test.js` pins to this one.
 */

export const THEMES = Object.freeze(["dark", "light"]);
/** What a browser with no stored preference gets, behind a sign-in. */
export const DEFAULT_THEME = "light";
/** What every path that needs no sign-in gets, whatever is stored. */
export const PUBLIC_THEME = "dark";
/** The localStorage key. `index.html`'s boot script reads the same one. */
export const STORAGE_KEY = "hans-theme";

/**
 * Paths that need no sign-in — always dark. Anchored at the start so
 * `/instructors/:id/programs` (signed-in) is not caught by `/programs` (the
 * public directory). `index.html`'s boot script carries this SAME expression;
 * change one, change both (the test compares them).
 */
export const PUBLIC_PATH_RE =
  /^\/(?:pricing|features|for-organizations|feedback|contact|privacy|terms|security|support|recording-consent|clips|guide|student-guide|org-guide|viewer-guide|login|verify-email|forgot-password|reset-password|consent|book|programs|founding|portfolio|p|student\/login|student\/invite|student\/forgot-password|student\/reset-password|org\/login|org\/signup|org\/verify-email|org\/forgot-password|org\/reset-password|parent\/login|parent\/forgot-password|parent\/reset-password|ops\/login|ops\/forgot-password|ops\/reset-password)(?:\/|$)|^\/$/;

/** @returns {boolean} Whether `pathname` is reachable without signing in. */
export function isPublicPath(pathname) {
  return PUBLIC_PATH_RE.test(pathname || "/");
}

/**
 * The theme a path should show: dark on every public path, otherwise the
 * stored preference (or the default).
 */
export function resolveTheme(pathname) {
  if (isPublicPath(pathname)) return PUBLIC_THEME;
  return readStoredTheme() || DEFAULT_THEME;
}

const currentPath = () => (typeof window !== "undefined" ? window.location.pathname : "/");

/** Browser-chrome colour (the PWA/status bar) for each theme. */
const THEME_COLOR = { dark: "#08080a", light: "#f3f1ec" }; // brand-lint-ignore: browser chrome, not a painted surface

const listeners = new Set();
let storageSyncArmed = false;

/** @returns {boolean} Whether `value` names a theme this build knows. */
export function isTheme(value) {
  return THEMES.includes(value);
}

/**
 * The stored preference, or null when there is none (or storage is blocked —
 * a private window, a locked-down browser; either way the answer is "no
 * preference", never a throw).
 */
export function readStoredTheme() {
  try {
    const value = window.localStorage.getItem(STORAGE_KEY);
    return isTheme(value) ? value : null;
  } catch {
    return null;
  }
}

/**
 * The theme currently applied to the document.
 *
 * Reads the attribute first because that is what the CSS is actually keyed
 * on, then the stored preference, then the default — so a render before
 * `initTheme` runs still reports what the boot script painted.
 */
export function getTheme() {
  if (typeof document === "undefined") return DEFAULT_THEME;
  const applied = document.documentElement.dataset.theme;
  if (isTheme(applied)) return applied;
  return readStoredTheme() || DEFAULT_THEME;
}

/** Stamp the document: the attribute the CSS keys on, plus the browser chrome. */
export function applyTheme(theme) {
  const resolved = isTheme(theme) ? theme : DEFAULT_THEME;
  const root = document.documentElement;
  root.dataset.theme = resolved;
  // Native form controls, scrollbars and the default focus ring follow this.
  root.style.colorScheme = resolved;
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute("content", THEME_COLOR[resolved]);
  return resolved;
}

function notify() {
  const current = getTheme();
  for (const listener of listeners) listener(current);
}

/**
 * Choose a theme: persist it, apply it, and tell every subscriber.
 *
 * An unknown value falls back to the default rather than throwing — the
 * value can come from storage another tab wrote, and a bad one must not take
 * the page down.
 *
 * @param {string} next "dark" | "light"
 * @returns {string} The theme actually applied.
 */
export function setTheme(next) {
  const theme = isTheme(next) ? next : DEFAULT_THEME;
  try {
    window.localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    // Storage is blocked; the choice still applies for this page's lifetime.
  }
  // The switch only ever renders behind a sign-in, so this is normally the
  // theme just chosen — but a public path stays dark regardless.
  applyTheme(isPublicPath(currentPath()) ? PUBLIC_THEME : theme);
  notify();
  return theme;
}

/**
 * Re-resolve the theme for a path — called on every client-side navigation
 * (see components/ThemeRouteSync), which is what makes signing out onto the
 * landing page, or opening /pricing from Settings, land on a dark page.
 */
export function syncThemeToRoute(pathname) {
  const next = resolveTheme(pathname);
  if (document.documentElement.dataset.theme !== next) {
    applyTheme(next);
    notify();
  }
  return next;
}

/**
 * Subscribe to theme changes. Returns the unsubscribe function — the shape
 * `useSyncExternalStore` expects.
 */
export function subscribe(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Apply the stored preference (idempotent — the boot script has usually
 * already done it) and start following changes made in another tab, so two
 * open windows never disagree about what colour the app is.
 */
export function initTheme() {
  applyTheme(resolveTheme(currentPath()));
  if (storageSyncArmed || typeof window === "undefined") return;
  storageSyncArmed = true;
  window.addEventListener("storage", (event) => {
    if (event.key !== STORAGE_KEY) return;
    applyTheme(resolveTheme(currentPath()));
    notify();
  });
}
